import { AxiosResponse } from 'axios';
import config from '../../config/configuration';
import { endpoints } from '../core/apiConfig';
import client from '../core/Interceptor';
import { getCookie } from 'cookies-next';
import { getAddressByBusinessId, getVisibilityParams, removeEmptyKeys } from '../../utils/UtilFunctions';
import { ReceiptPayload } from '../../app/(routes)/online-ordering/types';
import { ModifierGroup } from '@zeus/shared';
import { TicketsQueryParams } from '../menu/menu.type';

export const addToCartItem = async (obj: object) => {
  const response = await client.post(endpoints.cart.cartItem, obj);
  return response;
};

export const getCart = async (
  phoneOrSession: string,
  restaurantId: number,
  type: string,
  orderType: string,
  orderSource: string,
  guestSessionId?: string
) => {
  if (phoneOrSession) {
    const token = getCookie('AccessToken') ?? '';
    let url = `${config.BASE_URL}${endpoints.cart.getCartUrl}/${phoneOrSession}?restaurantId=${restaurantId}&type=${type}&orderType=${orderType}&orderSource=${orderSource}`;
    if (guestSessionId) {
      url += `&guestSessionId=${guestSessionId}`;
    }
    const response = await fetch(url, {
      cache: 'no-store',
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.json();
  }
};

export const getCheckoutCart = async (cartId: number) => {
  if (cartId) {
    const restaurant = getAddressByBusinessId();

    const token = getCookie('AccessToken') ?? '';
    const response = await fetch(
      `${config.BASE_URL}${endpoints.cart.getCartUrl}/${cartId}/checkout?restaurantId=${restaurant?.id}`,
      {
        cache: 'no-store',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.json();
  }
};

export const getNuggetCheckoutCart = async (token: string) => {
  if (token) {
    const response = await fetch(
      `${config.BASE_URL}${endpoints.cart.getCartUrl}/nugget/payment/get-nugget-cart?token=${token}`,
      {
        cache: 'no-store',
      }
    );
    return response.json();
  }
};

export const getCheckoutOrderStatus = async (cartId: number) => {
  if (cartId) {
    const restaurant = getAddressByBusinessId();
    if (restaurant && restaurant.id) {
      const response: AxiosResponse = await client.get(
        `${config.BASE_URL}${endpoints.cart.getCartUrl}/${cartId}/checkout/status?restaurantId=${restaurant.id}`
      );
      return response?.data;
    }
  }
};

export const getCheckoutTicketStatus = async (ticketId: number) => {
  if (ticketId) {
    const restaurant = getAddressByBusinessId();
    if (restaurant && restaurant.id) {
      const response: AxiosResponse = await client.get(
        `${config.BASE_URL}${endpoints.ticket.getTicketStatus}/${ticketId}/checkout/status?restaurantId=${restaurant?.id}`
      );
      return response?.data?.data;
    }
  }
};

export const getOrdersByTickets = async (ticketIds: number[]) => {
  if (ticketIds && ticketIds.length) {
    const restaurant = getAddressByBusinessId();
    const queryParams = {
      ticketIds,
      restaurantId: restaurant?.id,
    };

    Object.keys(queryParams).forEach(
      (key) =>
        queryParams[key as keyof TicketsQueryParams] == null && delete queryParams[key as keyof TicketsQueryParams]
    );

    const token = getCookie('AccessToken') ?? '';
    const response: AxiosResponse = await client.get(`${config.BASE_URL}${endpoints.cart.getCartsByTickets}`, {
      params: queryParams,
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data;
  }
};

export const getOrderStatusByTickets = async (ticketIds: number[]) => {
  if (ticketIds && ticketIds.length) {
    const restaurant = getAddressByBusinessId();
    const queryParams = {
      ticketIds,
      restaurantId: restaurant?.id,
    };

    Object.keys(queryParams).forEach(
      (key) =>
        queryParams[key as keyof TicketsQueryParams] == null && delete queryParams[key as keyof TicketsQueryParams]
    );

    const token = getCookie('AccessToken') ?? '';
    const response: AxiosResponse = await client.get(`${config.BASE_URL}${endpoints.cart.getCartsStatusByTickets}`, {
      params: queryParams,
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data;
  }
};

export const getItemInStock = async (restaurantId: number, itemId: number, selectedModifiers: ModifierGroup[]) => {
  const response = await client.post(
    `${config.BASE_URL}${endpoints.cart.getCartItem}/${restaurantId}/${itemId}`,
    selectedModifiers
  );
  return response;
};

export const getCartIsValid = async (restaurantId: any, cartId: any, tableId?: any) => {
  const token = getCookie('AccessToken') ?? '';
  let url = tableId
    ? `${config.BASE_URL}${endpoints.cart.getCartIsValid}/${restaurantId}/${cartId}?tableId=${tableId}`
    : `${config.BASE_URL}${endpoints.cart.getCartIsValid}/${restaurantId}/${cartId}?1=1`; // Added ?1=1 to simplify appending further query parameters

  const visibilityParams = getVisibilityParams();
  const newObj = removeEmptyKeys({
    ...visibilityParams,
  });

  const queryParams = new URLSearchParams(newObj as any).toString();
  url += `&${queryParams}`;

  const response = await fetch(url, {
    cache: 'no-store',
    headers: { Authorization: `Bearer ${token}` },
  });

  return response.json();
};

export const getNuggetIsValid = async (restaurantId: any, cartId: any, tableId?: any) => {
  const token = getCookie('AccessToken') ?? '';
  const url = tableId
    ? `${config.BASE_URL}${endpoints.cart.getNuggetCartIsValid}/${restaurantId}/${cartId}?tableId=${tableId}`
    : `${config.BASE_URL}${endpoints.cart.getNuggetCartIsValid}/${restaurantId}/${cartId}`;
  const response = await fetch(url, {
    cache: 'no-store',
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.json();
};

export const getLocalCartIsValid = async (restaurantId: any, businessId: any, tableId?: any) => {
  const token = getCookie('AccessToken') ?? '';
  const url = tableId
    ? `${config.BASE_URL}${endpoints.cart.getLocalCartIsValid}/${restaurantId}?tableId=${tableId}`
    : `${config.BASE_URL}${endpoints.cart.getLocalCartIsValid}/${restaurantId}`;
  const response = await client.post(
    url,
    { businessId },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return response;
};

export const getCartValidation = async (restaurantId: any, itemIds: number[] = [], isQSR?: boolean) => {
  const visibilityParams = getVisibilityParams(undefined, undefined, isQSR);
  const newObj = removeEmptyKeys({
    ...visibilityParams,
    itemIds,
  });
  const token = getCookie('AccessToken') ?? '';
  const response = await client.get(`${config.BASE_URL}${endpoints.cart.getCartValidation}/${restaurantId}`, {
    headers: { Authorization: `Bearer ${token}` },
    params: newObj,
  });
  return response;
};

export const updateCartItem = async (obj: object, cartItemId: number) => {
  const response = await client.patch(`${endpoints.cart.cartItem}/${cartItemId}`, obj);
  return response;
};
export const updateCartCustomer = async (
  restaurantId: number,
  guestId: string,
  customerId: string,
  orderType: string
) => {
  if (restaurantId && guestId && customerId) {
    const response = await client.patch(
      `${endpoints.cart.updateCartCustomer}/${guestId}/${customerId}?restaurantId=${restaurantId}&orderType=${orderType}`
    );
    return response;
  }
};
export const updateCart = async (obj: object, cartId: number) => {
  try {
    const newObj = removeEmptyKeys(obj) as any;
    if (obj && 'note' in obj) {
      newObj.note = obj.note;
    }
    const restaurant = getAddressByBusinessId();
    const response = await client.patch(
      `${endpoints.cart.updateCart}/${cartId}?restaurantId=${restaurant?.id}`,
      newObj
    );
    return response;
  } catch (error) {
    console.log(error, 'error from updateCart ');
    return null;
  }
};
export const deleteCart = async (cartItemId: number, restaurantId: number) => {
  const response = await client.delete(`${endpoints.cart.cartItem}/${cartItemId}?restaurantId=${restaurantId}`);
  return response;
};
export const checkOutCart = async (cartId: number) => {
  const response = await client.post(`${endpoints.cart.checkoutCart}/${cartId}`);
  return response;
};

export const sendNuggetEvent = async (payload: any) => {
  const response = await client.post(`${endpoints.cart.sendEvent}`, payload);
  return response;
};

export const updateCartStatus = async (payload: any) => {
  const response = await client.patch(`${endpoints.cart.updateCartStatus}`, payload);
  return response;
};

export const createReceipt = async (paymentId: number, payload: ReceiptPayload) => {
  if (paymentId) {
    const response = await client.post(`${endpoints.checkout.createReceipt}/${paymentId}`, payload);
    return response;
  }
};
